import { useWorkOrder } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import {
  ComplementOriginTranslator,
  WorkOrder,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useWorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { WorkOrderComplement } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { WorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { useCompaniesByLoggedEmployee } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { createContext, PropsWithChildren, useContext } from 'react';
import { QueryObserverResult } from 'react-query';
import moment from 'moment';
import { WorkOrderComplementFormData } from '../../../Complements/ComplementForm';

interface ComplementCloseContextProps {
  workorder: {
    data?: WorkOrder;
    isLoading: boolean;
    isFetching: boolean;
    refetch: VoidFunction;
    dataUpdatedAt: number;
  };
  complement: {
    data?: WorkOrderComplement;
    isLoading: boolean;
    refetch: () => Promise<QueryObserverResult<WorkOrderComplement, unknown>>;
    dataUpdatedAt: number;
  };
  companyConfig?: WorkorderConfigurationCompany;
  isAlerted: boolean;
  formatFormComplementData: () => WorkOrderComplementFormData | undefined;
}

type Props = PropsWithChildren<{
  workOrderId: number;
  complementId: number;
}>;

const ComplementCloseContext = createContext({} as ComplementCloseContextProps);

export const useComplementCloseContext = () =>
  useContext(ComplementCloseContext);

const ComplementCloseProvider: React.FC<Props> = ({
  workOrderId,
  complementId,
  children,
}) => {
  const { data: workorderData, ...workorderParams } = useWorkOrder(workOrderId);
  const { data: complementData, ...complementParams } =
    useWorkOrderComplement(complementId);

  const companyId = workorderData?.company.id;
  const customer = workorderData?.customer;

  const { data: companyConfig } =
    useWorkorderConfigurationCompanyQuery(companyId);

  const { data: companiesData } = useCompaniesByLoggedEmployee();
  const companies = flatInfiniteData(companiesData);

  const isAlerted = !!customer && !customer.showLgpd && !customer.enableLgpd;

  function formatFormComplementData() {
    if (workorderData && complementData) {
      let formObj: WorkOrderComplementFormData = {
        ...complementData,
        origin: complementData?.origin
          ? ComplementOriginTranslator[complementData.origin]
          : undefined,
        companyId: workorderData.company.id,
        complementId: complementData.id,
        estimatorId: complementData.estimator?.id,
        entryDate: complementData.entryDate
          ? moment(complementData.entryDate)
          : undefined,
        authorizationDate: complementData.authorizationDate
          ? moment(complementData.authorizationDate)
          : undefined,
        closingDate: complementData.closingDate
          ? moment(complementData.closingDate)
          : undefined,
        estimatedLeaveDate: complementData.estimatedLeaveDate
          ? moment(complementData.estimatedLeaveDate)
          : undefined,
        returnDate: complementData.returnDate
          ? moment(complementData.returnDate)
          : undefined,
        lastOilChange: complementData.lastOilChange
          ? moment(complementData.lastOilChange)
          : undefined,
        scheduleDateTime: complementData.scheduleDateTime
          ? moment(complementData.scheduleDateTime)
          : undefined,
      };

      const company = companies?.find(
        (company) => company.id === workorderData.company.id,
      );
      if (company) {
        formObj = { ...formObj, companyId: company.id };
      }

      return formObj;
    }
  }

  return (
    <ComplementCloseContext.Provider
      value={{
        workorder: {
          data: workorderData,
          isLoading: workorderParams.isLoading,
          isFetching: workorderParams.isFetching,
          refetch: workorderParams.refetch,
          dataUpdatedAt: workorderParams.dataUpdatedAt,
        },
        complement: {
          data: complementData,
          isLoading: complementParams.isLoading,
          refetch: complementParams.refetch,
          dataUpdatedAt: complementParams.dataUpdatedAt,
        },
        companyConfig,
        isAlerted,
        formatFormComplementData,
      }}
    >
      {children}
    </ComplementCloseContext.Provider>
  );
};

export default ComplementCloseProvider;
